import * as Sentry from '@sentry/vue';
import { endOfDay, formatISO, isWithinInterval, startOfDay } from 'date-fns';
import { getTimezoneOffset } from 'date-fns-tz';
import { getExtendedVisitorData } from '../utils/fingerprint';
import store from '@/store/index';
import { OPERATING_SYSTEMS } from './constants';

export const isEmptyObj = (obj) => {
  for (let prop in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, prop)) return false;
  }
  return true;
};

export const getStorageItem = (key, empty) => {
  if (typeof window === 'undefined') return empty;

  try {
    const item = window.localStorage.getItem(key);
    // if not default value provided (e.g. string, number)
    if (typeof empty !== 'boolean' && !empty) return item;
    if (item) return JSON.parse(item);
  } catch (err) {
    Sentry.withScope(function (scope) {
      scope.setLevel(Sentry.Severity.Fatal);
      Sentry.captureException(err);
    });
    console.log('Storage item parsing error: ', err);
  }
  // return default value for object
  return empty;
};

export const setStorageItem = (key, value) => {
  if (typeof window === 'undefined') return;

  let item = value;
  if (typeof value === 'object') item = JSON.stringify(value);

  if (value === null || value === undefined)
    window.localStorage.removeItem(key);
  else window.localStorage.setItem(key, item);
};

export const getSessionItem = (key, empty) => {
  if (typeof window === 'undefined') return empty;

  try {
    const item = window.sessionStorage.getItem(key);

    if (typeof empty !== 'boolean' && !empty) return item;
    if (item) return JSON.parse(item);
  } catch (err) {
    Sentry.withScope(function (scope) {
      scope.setLevel(Sentry.Severity.Fatal);
      Sentry.captureException(err);
    });
    console.log('Session storage parsing error: ', err);
  }

  return empty;
};

export const setSessionItem = (key, value) => {
  if (typeof window === 'undefined') return;

  let item = value;
  if (typeof value === 'object') item = JSON.stringify(value);

  if (typeof value === 'undefined' || value === null)
    window.sessionStorage.removeItem(key);
  else window.sessionStorage.setItem(key, item);
};

export const debounce = (fn, delayTime = 1000) => {
  let timer = null;
  return function () {
    clearTimeout(timer);
    let args = arguments;
    let that = this;
    timer = setTimeout(function () {
      fn.apply(that, args);
    }, delayTime);
  };
};

export const compareFn = (a, b, dir = true) => {
  if (a > b) return dir ? 1 : -1;
  if (a < b) return dir ? -1 : 1;
  return 0;
};

export const compareFullName = (a, b, lastFirst = false) => {
  if (!a.lastName || !b.lastName) {
    return 0;
  }

  const firstKey = lastFirst ? 'lastName' : 'firstName';
  const secondKey = lastFirst ? 'firstName' : 'lastName';

  // Perform case-insensitive comparisons using toLowerCase()
  let comparison = a[firstKey]
    .toLowerCase()
    .localeCompare(b[firstKey].toLowerCase());
  if (comparison === 0) {
    comparison = a[secondKey]
      .toLowerCase()
      .localeCompare(b[secondKey].toLowerCase());
  }

  return comparison;
};

export const compareLastNames = (a, b, dir = true) => {
  if (!a.lastName || !b.lastName) {
    return 0;
  }

  const compareLastNames = a.lastName.localeCompare(b.lastName);
  if (compareLastNames !== 0) {
    return dir ? compareLastNames : -compareLastNames;
  }

  const compareFirstNames = a.firstName.localeCompare(b.firstName);
  return dir ? compareFirstNames : -compareFirstNames;
};

export const getBrowser = async () => {
  try {
    const extendedVisitorData = await getExtendedVisitorData();
    return extendedVisitorData.browserName;
  } catch (e) {
    var userAgent = navigator.userAgent;

    if (userAgent.indexOf('Chrome') !== -1) {
      return 'Chrome';
    } else if (userAgent.indexOf('Firefox') !== -1) {
      return 'Firefox';
    } else if (userAgent.indexOf('Safari') !== -1) {
      return 'Safari';
    } else if (userAgent.indexOf('Edge') !== -1) {
      return 'Edge';
    } else if (
      userAgent.indexOf('Opera') !== -1 ||
      userAgent.indexOf('OPR') !== -1
    ) {
      return 'Opera';
    } else if (userAgent.indexOf('Trident') !== -1) {
      return 'Internet Explorer';
    } else {
      return 'Unknown';
    }
  }
};

export const isMobile = (os) => {
  return (
    os.toLowerCase() == OPERATING_SYSTEMS.IOS.toLowerCase() ||
    os.toLowerCase() == OPERATING_SYSTEMS.ANDROID.toLowerCase()
  );
};

export const getOS = async () => {
  try {
    const extendedVisitorData = await getExtendedVisitorData();
    return extendedVisitorData.os;
  } catch (e) {
    // TODO: Wrap code below in this catch
    console.log(e);
  }

  let platform;

  // Check for navigator.userAgentData and its platform
  if (navigator.userAgentData && navigator.userAgentData.platform) {
    platform = navigator.userAgentData.platform;
  } else {
    // Fall back to navigator.platform if userAgentData is not available
    platform = navigator.platform;
  }

  // Normalize platform to return specific OS names
  switch (platform) {
    case 'macOS':
    case 'MacIntel':
      return 'Mac OS';
    case 'iOS':
    case 'iPhone':
    case 'iPad':
      return 'iOS';
    case 'Windows':
    case 'Win32':
    case 'Win64':
      return 'Windows';
    case 'Android':
      return 'Android';
    case 'Linux':
    case 'Linux armv81':
      // Additional check for Android devices that return 'Linux' or 'Linux armv81'
      if (/Android/i.test(navigator.userAgent)) {
        return 'Android';
      }
      return 'Other';
    default:
      return 'Other';
  }
};

export const getFullName = (v, externalAttributes) => {
  const { firstName = '', middleName = '', lastName = '' } = v;
  // Incorporate external attributes into name
  let externalAttribute = '';
  if (externalAttributes && v.externalAttributes) {
    // If there are multiple external attributes, then seperate by '-'
    // TODO: this method needs to be renamed, and optimized do to the .find()
    externalAttribute = externalAttributes.reduce(
      (accumulator, attributeName) => {
        const matchedAttribute = v.externalAttributes.find(
          (ea) => ea.name === attributeName
        )?.value;
        if (matchedAttribute) {
          return (
            (accumulator ? accumulator + ' - ' : accumulator) +
            v.externalAttributes.find((ea) => ea.name === attributeName)?.value
          );
        }
        return accumulator;
      },
      ''
    );
    // Wrap in ()
    if (externalAttribute) {
      externalAttribute = '(' + externalAttribute + ')';
    }
  }

  const displayLastNameBeforeFirstName =
    store.getters['organization/displayLastNameBeforeFirstName'];

  if (!middleName) {
    if (displayLastNameBeforeFirstName) {
      return `${lastName}${
        lastName ? ',' : ''
      } ${firstName} ${externalAttribute}`;
    } else {
      return `${firstName} ${lastName} ${externalAttribute}`;
    }
  }

  const abbr =
    middleName.length > 2 ? middleName.slice(0, 2) + '.' : middleName;

  if (displayLastNameBeforeFirstName) {
    return `${lastName}${
      lastName ? ',' : ''
    } ${firstName} ${abbr}  ${externalAttribute}`;
  } else {
    return `${firstName} ${abbr} ${lastName} ${externalAttribute}`;
  }
};

export const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const getNameAbbr = (v) => {
  const { firstName = '', lastName = '' } = v;
  return (firstName ? firstName[0] : '') + (lastName ? lastName[0] : '');
};

export const parseJwt = (jwt) => {
  try {
    if (!jwt) return null;

    return JSON.parse(atob(jwt.split('.')[1]));
  } catch (err) {
    Sentry.captureException(err);
    console.log('error while parsing jwt token: ', err);
    return null;
  }
};

export const generateUrl = (hostURL = '', obj = {}) => {
  if (typeof obj !== 'object' || !Object.keys(obj).length) return hostURL;

  let params = new URLSearchParams();
  for (let key in obj) {
    if (
      typeof obj[key] === 'undefined' ||
      obj[key] === null ||
      !obj[key].toString()
    )
      continue;

    if (Array.isArray(obj[key])) {
      for (let item of obj[key]) {
        params.append(key, item);
      }
    } else {
      params.append(key, obj[key]);
    }
  }

  if (![...Array.from(params.entries())].length) return hostURL;

  return `${hostURL}?${params.toString()}`;
};

export const isDefined = (v) => typeof v !== 'undefined' && v !== null;

export const capitalize = ([first, ...rest]) => {
  return first.toUpperCase() + rest.join('');
};

export const binaryToBase64DataURL = (blob) =>
  new Promise((resolvePromise) => {
    const reader = new FileReader();
    reader.onload = () => resolvePromise(reader.result);
    reader.readAsDataURL(blob);
  });

export const isLocalEnvironment = () => {
  // Without .toString() compiler optimizer will change condition to true
  return process.env.VUE_APP_HOST.toString() !== 'deployed';
};

export const getDateTimeStringWithoutTimezone = (date) => {
  const isoDateWithLocalTimezone =
    typeof date === 'string' ? date : formatISO(date);

  if (isoDateWithLocalTimezone.includes('Z')) {
    return isoDateWithLocalTimezone.replace('Z', '');
  }

  // If already missing timezone then return
  if (isoDateWithLocalTimezone.length != 25) {
    return isoDateWithLocalTimezone;
  }

  // Strip '-07:00' for example
  return isoDateWithLocalTimezone.substring(
    0,
    isoDateWithLocalTimezone.length - 6
  );
};
export const changeTimeZoneToPST = (date) => {
  const pstOffset = getTimezoneOffset('America/Los_Angeles') / (60 * 60 * 1000);
  if (pstOffset === -8) {
    return getDateTimeStringWithoutTimezone(date) + '-08:00';
  } else if (pstOffset === -7) {
    return getDateTimeStringWithoutTimezone(date) + '-07:00';
  } else {
    // If it's neither -8 or -7, something is wrong since we want PT
    throw new Error('unable to set time reliably');
  }
};

export const emailValidation = (email) => {
  const emailRegex =
    // eslint-disable-next-line
    /^(?:[A-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[A-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9]{2,}(?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])$/i;
  return emailRegex.test(email);
};

export const getFirstApiErrorIfAvailable = (error) => {
  return error.response?.data?.errors?.length
    ? error.response.data.errors[0].description
    : null;
};

export const intersection = (set1, set2) => {
  const result = new Set();
  for (const item of set1) {
    if (set2.has(item)) {
      result.add(item);
    }
  }
  return result;
};

export const getEndOfDay = (dateStr, isLocale) => {
  if (dateStr.indexOf('T') !== -1) {
    dateStr = dateStr.split('T')[0];
    if (isLocale) {
      try {
        const splitDate = dateStr.split('-');
        dateStr = `${splitDate[1]}/${splitDate[2]}/${splitDate[0]}`;
      } catch (e) {
        console.log(e);
      }
    }
  } else if (dateStr.indexOf('/') !== -1) {
    const slashes = /\//g;
    dateStr = dateStr.replace(slashes, '-');
  }
  return dateStr;
};

export const hashExternalAttributes = (
  externalAttributes,
  orgExternalAttributes = null
) => {
  if (!externalAttributes) {
    return {};
  }

  const hashedExternalAttributes = externalAttributes.reduce((acc, ea) => {
    acc[ea['name']] = ea.value;
    return acc;
  }, {});

  if (orgExternalAttributes) {
    orgExternalAttributes.forEach((ea) => {
      if (!hashedExternalAttributes[ea.name]) {
        hashedExternalAttributes[ea.name] = `No ${ea.displayName} found`;
      }
    });
  }

  return hashedExternalAttributes;
};

export const isTodayBetweenDates = (startDate, endDate) => {
  const today = new Date();
  const start = new Date(startDate);
  const end = new Date(endDate);

  const normalizedDate = startOfDay(today);
  const normalizedStart = startOfDay(start);
  const normalizedEnd = endOfDay(end);

  return isWithinInterval(normalizedDate, {
    start: normalizedStart,
    end: normalizedEnd,
  });
};
